import React, { useState } from "react";
import { useController } from "@rest-hooks/react";

import QRCodeScanner from "components/TicketsList/QRCodeScanner";
import CustomModal from "components/CustomModal";

import { useError } from "utils/useErrorController";
import { useToast } from "utils/context/ToastContext";
import CheckinMode from "utils/localStorage/CheckinMode";

// Shared scan → lookup → redeem state machine, used by both ticket check-in and
// merch collection. It owns the QR scanner + the details modal and the action
// call (single + group, with fast-track auto-action). Each surface supplies how
// to fetch a scanned item, how to label toasts, and how to render the modal body
// — everything ticket-specific (selling, end-sales, stats, columns) stays out.
export default function useScanRedeem({
  Resource,
  resourceParams,
  fetchItem,          // async ({ result, ...resourceParams }) => item
  forwardIntent,      // "check_in" | "collect"
  allowReverse,       // tickets: allow_checkout; merch: uncollect always allowed
  allowFastTrack = true,
  scanTitle,          // scanner dialog title / scan button label
  toastFor,           // (intent, amount) => string
  buildModalContent,  // (item, handleAction) => CustomModal content object
  onDone,             // () => void — refetch the list after an action
}) {
  const [modalContent, setModalContent] = useState(null);
  const [scannerOpen, setScannerOpen] = useState(false);

  const { fetch } = useController();
  const { handleError, setLoading } = useError();
  const { showToast } = useToast();

  const handleAction = async (uuid, showFastToast, intent, amount = 1) => {
    try {
      setLoading(true);

      const params = { ...resourceParams, intent };
      if (amount > 1 && intent === forwardIntent) params.target = "group";

      await fetch(Resource.update(), { id: uuid, ...params }, params);
      setLoading(false);

      showToast("success", toastFor(intent, amount), showFastToast, showFastToast);

      if (onDone) onDone();
      setModalContent(null);
    } catch (error) {
      handleError(error);
    }
  };

  const openDetails = (item) => {
    setModalContent(buildModalContent(item, handleAction));
  };

  // Open the modal from a list row: fetch the full detail payload first so the row
  // path gets the same data as a scan (e.g. the order's item breakdown), which the
  // index summary does not carry.
  const openDetailsByUuid = async (uuid) => {
    try {
      setLoading(true);
      const item = await fetchItem({ result: uuid, ...resourceParams });
      setLoading(false);
      openDetails(item);
    } catch (error) {
      setLoading(false);
      handleError(error);
    }
  };

  const handleQRCodeRead = async (result) => {
    try {
      setLoading(true);
      const item = await fetchItem({ result, ...resourceParams });

      const fastMode = allowFastTrack && CheckinMode.read() === "fast";
      const single = item.order_amount === 1;
      const reversible = allowReverse && item.redeem_state === "completed";

      // Fast-track only auto-acts on a single, not-yet-redeemed item.
      if (fastMode && single && !reversible) {
        handleAction(item.uuid, true, forwardIntent);
      } else {
        setLoading(false);
        openDetails(item);
      }
    } catch (error) {
      setLoading(false);
      setModalContent(null);
      handleError(error);
    }
  };

  const openScanner = () => setScannerOpen(true);

  const renderScannerAndModal = () => (
    <>
      {scannerOpen && (
        <QRCodeScanner
          open={scannerOpen}
          onClose={() => setScannerOpen(false)}
          onQRCodeRead={handleQRCodeRead}
          allowCheckout={allowReverse}
          allowFastTrack={allowFastTrack}
          title={scanTitle}
        />
      )}

      {modalContent && (
        <CustomModal
          handleClose={() => setModalContent(null)}
          customModalContent={modalContent}
        />
      )}
    </>
  );

  return { openScanner, openDetails, openDetailsByUuid, renderScannerAndModal };
}
